import { useEffect, useId, useRef, useState, type FormEvent, type KeyboardEvent as ReactKeyboardEvent, type ReactElement } from 'react';

import type { ProjectTemplateCaptureMode } from '../../persistence/project-template';

export interface ProjectTemplateExportInput {
  readonly captureMode: ProjectTemplateCaptureMode;
  readonly name: string;
  readonly description?: string;
}

interface ProjectTemplateExportDialogProps {
  readonly isOpen: boolean;
  readonly defaultName: string;
  readonly onCancel: () => void;
  readonly onExport: (input: ProjectTemplateExportInput) => void;
}

const CAPTURE_MODES: readonly { readonly mode: ProjectTemplateCaptureMode; readonly label: string; readonly hint: string }[] = [
  { mode: 'Planning', label: '規劃範本', hint: '清除進度、實際值與基準線後重新排程；日後建立專案時可平移排程錨點。' },
  { mode: 'Snapshot', label: '快照範本', hint: '原樣保存目前所有資料（含進度與基準線）；日後建立專案時不可平移日期。' },
];

export function ProjectTemplateExportDialog({ isOpen, defaultName, onCancel, onExport }: ProjectTemplateExportDialogProps): ReactElement | null {
  const [name, setName] = useState(defaultName);
  const [description, setDescription] = useState('');
  const [captureMode, setCaptureMode] = useState<ProjectTemplateCaptureMode>('Planning');
  const [error, setError] = useState<string | null>(null);
  const titleId = useId();
  const dialogRef = useRef<HTMLFormElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const wasOpenRef = useRef(false);
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      setName(defaultName);
      setDescription('');
      setCaptureMode('Planning');
      setError(null);
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, defaultName]);

  useEffect(() => {
    if (!isOpen) return undefined;
    returnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    nameInputRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onCancelRef.current();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      returnFocusRef.current?.focus();
      returnFocusRef.current = null;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (!name.trim()) {
      setError('請輸入範本名稱。');
      return;
    }
    onExport({
      captureMode,
      name: name.trim(),
      description: description.trim() || undefined,
    });
  };

  const trapFocus = (event: ReactKeyboardEvent<HTMLFormElement>): void => {
    if (event.key !== 'Tab') return;
    const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
      'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    if (!focusable || focusable.length === 0) return;
    const items = [...focusable];
    const first = items[0];
    const last = items[items.length - 1];
    if (!first || !last) return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4" role="presentation">
      <form
        aria-labelledby={titleId}
        aria-modal="true"
        className="w-full max-w-lg rounded-lg bg-white p-5 shadow-xl dark:bg-slate-900"
        ref={dialogRef}
        onKeyDown={trapFocus}
        onSubmit={handleSubmit}
        role="dialog"
      >
        <div className="mb-4 flex items-center justify-between gap-4">
          <h2 id={titleId} className="text-base font-semibold">匯出為範本</h2>
          <button type="button" onClick={onCancel} aria-label="關閉匯出範本" className="rounded px-2 py-1 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800">關閉</button>
        </div>

        <div className="grid gap-3 text-sm">
          <label className="grid gap-1">
            範本名稱
            <input ref={nameInputRef} aria-invalid={Boolean(error)} aria-describedby={error ? 'template-name-error' : undefined} value={name} onChange={(event) => setName(event.target.value)} className="rounded border border-slate-300 bg-transparent px-2 py-1.5 dark:border-slate-600" />
            {error ? <span id="template-name-error" className="text-xs text-red-600">{error}</span> : null}
          </label>
          <label className="grid gap-1">
            說明（選填）
            <textarea value={description} onChange={(event) => setDescription(event.target.value)} rows={2} className="resize-y rounded border border-slate-300 bg-transparent px-2 py-1.5 dark:border-slate-600" />
          </label>
          <fieldset className="grid gap-2">
            <legend className="mb-1">擷取模式</legend>
            {CAPTURE_MODES.map((option) => (
              <label key={option.mode} className="grid grid-cols-[auto_minmax(0,1fr)] items-start gap-2">
                <input type="radio" name="template-capture-mode" className="mt-1" checked={captureMode === option.mode} onChange={() => setCaptureMode(option.mode)} />
                <span>
                  {option.label}
                  <span className="block text-xs text-slate-500 dark:text-slate-400">{option.hint}</span>
                </span>
              </label>
            ))}
          </fieldset>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onCancel} className="rounded border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-100 dark:border-slate-600 dark:hover:bg-slate-800">取消</button>
          <button type="submit" className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700">匯出…</button>
        </div>
      </form>
    </div>
  );
}
